import React, { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Drawer, useDrawer, drawerBackdropId } from '@frontend/design-system';
import { usePanelStore, usePanelShallowStore } from './store';

//drawer should appear below settings modal
const drawerZIndex = theme.zIndex.modals - 1;

export const PanelLayout: React.FC = React.memo(() => {
  const { updateAvailableWidth, hasTrayPanels, heightOffset } = usePanelShallowStore(
    'updateAvailableWidth',
    'hasTrayPanels',
    'heightOffset'
  );
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const { modalProps } = useDrawer({
    open: hasTrayPanels,
    onOpenChange: (open) => {
      if (!open) {
        const trayIds = usePanelStore.getState().trayIds;
        const panelCloseFunctions = usePanelStore.getState().panelCloseFunctions;
        trayIds.forEach((id) => panelCloseFunctions[id]?.());
      }
    },
    outsidePress: (event) =>
      (event.target as HTMLElement).id === drawerBackdropId ||
      !!(event.target as HTMLElement).closest('#weave-top-bar'),
  });

  const resizeRafId = useRef<number | null>(null);

  useEffect(() => {
    function onResize() {
      if (resizeRafId.current) {
        cancelAnimationFrame(resizeRafId.current);
      }
      resizeRafId.current = requestAnimationFrame(() => {
        updateAvailableWidth(window.innerWidth);
      });
    }

    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeRafId.current) cancelAnimationFrame(resizeRafId.current);
    };
  }, [updateAvailableWidth]);

  return (
    <motion.div
      css={css`
        height: 100%;
      `}
    >
      <div
        id='panel-root'
        css={css`
          display: flex;
          height: 100%;
          isolation: isolate;
        `}
      />
      <Drawer
        keepMounted
        additionalHeightOffset={isXSmall ? 72 + heightOffset : 0}
        css={[
          css`
            display: flex;
            padding: 0;
            max-width: 100%;
          `,
          isXSmall
            ? css`
                width: 100%;
              `
            : css`
                width: auto;
              `,
        ]}
        {...modalProps}
        size={isXSmall ? 'large' : 'auto'}
        zIndex={drawerZIndex}
      >
        <motion.div
          css={css`
            display: flex;
            height: 100%;
          `}
          id='tray-root'
        />
      </Drawer>
    </motion.div>
  );
});
